import React from 'react';
import Checkbox from '@cx-web/components/primitives/Checkbox';
import TextInput from '@cx-web/components/primitives/TextInput';

import Banner from './Banner';
import { isValidEmailFormat } from './email';
import LoadingSpinner from './LoadingSpinner';
import styles from './MyOCLogin2018.module.css';
import { type SignupViewProps } from './types';

/**
 * Signup form ported from the legacy widget's `signup-view`. Mirrors the legacy field set (email,
 * password, marketing-consent toggle, CTA, T&C copy) — the legacy widget collected no name and never
 * rendered the state dropdown, so neither does this. Dropped vs legacy (NGF-6371 scope): social signup,
 * the non-legalised-state banner, and the BETA embed container.
 */
const SignupView: React.FC<SignupViewProps> = ({
  banner,
  defaultEmail = '',
  labelActions,
  loading = false,
  onSubmit,
}) => {
  const [email, setEmail] = React.useState(defaultEmail);
  const [password, setPassword] = React.useState('');
  const [marketingConsent, setMarketingConsent] = React.useState(true);
  const [isValidEmail, setIsValidEmail] = React.useState(true);
  const [emptyFields, setEmptyFields] = React.useState(false);

  const hasError = !isValidEmail || emptyFields || banner?.type === 'error';

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!email || !password) {
      setEmptyFields(true);
      return;
    }
    setEmptyFields(false);
    onSubmit({ email, marketingConsent, password });
  };

  if (loading) {
    return (
      <form className={styles.view}>
        <LoadingSpinner />
      </form>
    );
  }

  return (
    <form className={styles.view} onSubmit={handleSubmit}>
      <div className={styles.viewContent}>
        <div className={styles.viewTitle}>{labelActions.get('AUTH_SIGN_UP_TO_ODDSCHECKER')}</div>

        <TextInput
          hasError={hasError}
          name="email"
          onBlur={() => setIsValidEmail(isValidEmailFormat(email))}
          onChange={event => {
            setEmail(event.target.value);
            setEmptyFields(false);
          }}
          placeholder={labelActions.get('AUTH_INPUT_EMAIL')}
          type="email"
          value={email}
          wrapperClassName={styles.field}
        />
        {!isValidEmail && <div className={styles.errorMessage}>{labelActions.get('AUTH_EMAIL_ADDRESS_FORMAT')}</div>}

        <TextInput
          hasError={hasError}
          name="password"
          onChange={event => {
            setPassword(event.target.value);
            setEmptyFields(false);
          }}
          placeholder={labelActions.get('AUTH_INPUT_PASSWORD')}
          type="password"
          value={password}
          wrapperClassName={styles.field}
        />

        {banner && <Banner banner={banner} resendLabel={labelActions.get('AUTH_RESEND_EMAIL')} />}

        <div className={styles.buttonWrapper}>
          <Checkbox
            checked={marketingConsent}
            className={styles.marketingTick}
            onClick={() => setMarketingConsent(value => !value)}
            testId="myoc-auth-marketing-consent"
          >
            <span className={styles.marketingLabel}>{labelActions.get('AUTH_YES_TO_MARKETING')}</span>
          </Checkbox>
          <button className={styles.ctaButton} type="submit">
            {labelActions.get('AUTH_SIGN_UP_CTA')}
          </button>
          {/* The T&C copy is a localised string containing links to the policy pages. */}
          <p
            className={styles.termsCopy}
            dangerouslySetInnerHTML={{ __html: labelActions.get('AUTH_I_HAVE_READ_TANDC_AND_PRIVACY_POLICY') }}
          />
        </div>
      </div>
    </form>
  );
};

export default SignupView;
